import { Dispatch } from "redux"
import { ValidationError } from "sequelize"
import {
    CcReviewReviewItem,
    CcReviewReviewItemCreate,
    CcReviewReviewItemSave,
    CcReviewReviewRevision,
    CcReviewReviewRevisionSave,
    CcReviewReviewItemState,
    CcReviewReviewHistory,
    CcReviewReviewHistoryCreate,
    CcReviewReviewHistoryUpdateResponse,
    CcReviewReviewHistoryImportProps,
    CcReviewReviewTempAnswer,
} from "server/models"

import {
    GET, POST, DELETE, validate, sort, sortByRevision,
} from "client/store/utils"
import { OrderType } from "client/utils"
import { LangType } from "client/store"
import {
    ReviewReviewItemList,
    ReviewReviewTempAnswerByUser,

    ReviewReviewItemValidation,
    ReviewReviewRevisionValidation,
    ReviewReviewHistoryValidation,
    ReviewReviewTempAnswerValidation,
} from "./types"
import {
    setCcReviewReviewItemStateList,
    setCcReviewReviewItemHistoryList,
    setCcReviewReviewTempAnswerList,
} from "./actions"

// review item

export const LoadCcReviewReviewItems = (projectId: number, fileId: number) => (
    GET(`/ccReviewReviewItem/projectId/${projectId}/fileId/${fileId}`)
    .then((items: CcReviewReviewItem[]) => {
        const itemList: ReviewReviewItemList = {}
        items.forEach((item) => {
            itemList[item.id] = item
        })
        return { itemList, items }
    })
)

export const LoadCcReviewReviewItemById = (itemId: number, fileId: number) => (
    GET(`/ccReviewReviewItem/id/${itemId}/fileId/${fileId}`)
    .then((item: CcReviewReviewItem) => item)
)

export const SaveCcReviewReviewItem = (
    id: number,
    item: CcReviewReviewItemCreate,
    lang: LangType,
) => {
    const data: CcReviewReviewItemSave = {
        id,
        item,
    }

    return POST(
        "/ccReviewReviewItem/save",
        JSON.stringify(data),
    ).then((res) => {
        const newItem = res as CcReviewReviewItem
        const error = res as ValidationError

        return {
            newItemId: newItem.id || null,
            validation: <ReviewReviewItemValidation>validate(error, lang),
        }
    })
}

export const DeleteCcReviewReviewItem = (itemId: number) => (
    DELETE(`/ccReviewReviewItem/delete/${itemId}`)
    .then((removedRows: number) => removedRows)
)

export const SortCcReviewReviewItems = (
    items: CcReviewReviewItem[],
    sortColumn: keyof CcReviewReviewItem,
    sortOrder: OrderType,
) => {
    if (sortColumn === "version") {
        items.sort((a, b) => sortByRevision(a.version.revision, b.version.revision, sortOrder))
    } else {
        items.sort((a, b) => sort(a, b, sortColumn, sortOrder))
    }

    const itemIds: number[] = []
    items.forEach((item) => itemIds.push(item.id))

    return itemIds
}

// revisions

export const LoadCcReviewReviewItemRevisionData = (
    itemId: number,
    revisionId: number,
    dispatch: Dispatch,
) => (
    Promise.all([
        LoadCcReviewReviewItemState(itemId, revisionId).then((res) => {
            dispatch(setCcReviewReviewItemStateList(itemId, res))
        }),
        LoadCcReviewReviewHistory(itemId, revisionId).then((res) => {
            dispatch(setCcReviewReviewItemHistoryList(itemId, res))
        }),
        LoadCcReviewReviewTempAnswers(itemId, revisionId).then((res) => {
            dispatch(setCcReviewReviewTempAnswerList(itemId, res))
        }),
    ])
)
export const LoadCcReviewReviewRevisions = (itemId: number) => (
    GET(`/ccReviewReviewRevision/itemId/${itemId}`)
    .then((revisions: CcReviewReviewRevision[]) => revisions)
)
export const CreateCcReviewReviewRevision = (itemId: number) => (
    GET(`/ccReviewReviewRevision/create/itemId/${itemId}`)
    .then((revision: CcReviewReviewRevision) => revision)
)
export const UpdateCcReviewReviewRevision = (id: number, name: string, lang: LangType) => {
    const data: CcReviewReviewRevisionSave = { id, name }
    return POST(
        "/ccReviewReviewRevision/update",
        JSON.stringify(data),
    ).then((res) => {
        const error = res as ValidationError
        const validation = <ReviewReviewRevisionValidation>validate(error, lang)

        return validation
    })
}
export const DeleteCcReviewReviewRevision = (revisionId: number) => (
    DELETE(`/ccReviewReviewRevision/delete/${revisionId}`)
    .then((removedRows: number) => removedRows)
)

// state

export const LoadCcReviewReviewItemState = (itemId: number, revisionId: number) => (
    GET(`/ccReviewReviewItemState/itemId/${itemId}/revision/${revisionId}`)
    .then((state: CcReviewReviewItemState) => state)
)
export const UpdateCcReviewReviewVerifier = (data: CcReviewReviewItemState[]) => (
    POST(
        "/ccReviewReviewItemState/verifier",
        JSON.stringify(data),
    )
)
export const UpdateCcReviewReviewState = (data: CcReviewReviewItemState) => (
    POST(
        "/ccReviewReviewItemState/update",
        JSON.stringify(data),
    )
)
export const CreateCcReviewReviewState = (data: CcReviewReviewItemState) => (
    POST(
        "/ccReviewReviewItemState/create",
        JSON.stringify(data),
    )
)

// history

export const CreateManyCcReviewReviewHistory = (
    historyArray: CcReviewReviewHistoryCreate[],
) => (
    POST(
        "/ccReviewReviewHistory/bulkCreate",
        JSON.stringify(historyArray),
    )
)
export const CreateCcReviewReviewHistory = (
    history: CcReviewReviewHistoryCreate,
    lang: LangType,
) => (
    POST(
        "/ccReviewReviewHistory/create",
        JSON.stringify(history),
    ).then((res) => {
        const newHistory = res as CcReviewReviewHistory
        const error = res as ValidationError
        const validation = <ReviewReviewHistoryValidation>validate(error, lang)

        return {
            newHistoryId: newHistory.id || null,
            validation,
        }
    })
)
export const UpdateCcReviewReviewHistoryResponse = ( // for Super Admin only
    id: number,
    responseId: number,
    lang: LangType,
) => {
    const data: CcReviewReviewHistoryUpdateResponse = {
        id,
        responseId,
    }
    return POST(
        "/ccReviewReviewHistory/updateResponse",
        JSON.stringify(data),
    ).then((res) => {
        const error = res as ValidationError
        const validation = <ReviewReviewHistoryValidation>validate(error, lang)
        return validation
    })
}
export const LoadCcReviewReviewHistory = (itemId: number, revisionId: number) => (
    GET(`/ccReviewReviewHistory/item/${itemId}/revision/${revisionId}`)
    .then((history: CcReviewReviewHistory[]) => history)
)
export const ImportCcReviewReviewHistory = (data: CcReviewReviewHistoryImportProps) => (
    POST(
        "/ccReviewReviewHistory/import",
        JSON.stringify(data),
    )
)

// temp answer

export const LoadCcReviewReviewTempAnswers = (
    itemId: number,
    revisionId: number,
) => (
    GET(`/ccReviewReviewTempAnswer/item/${itemId}/revision/${revisionId}`)
    .then((answers: CcReviewReviewTempAnswer[]) => {
        const tempAnswers: ReviewReviewTempAnswerByUser = {}
        answers.forEach((answer) => {
            tempAnswers[answer.editorId] = answer
        })
        return tempAnswers
    })
)
export const SaveCcReviewReviewTempAnswer = (
    answer: CcReviewReviewTempAnswer,
    lang: LangType,
) => (
    POST(
        "/ccReviewReviewTempAnswer/save",
        JSON.stringify(answer),
    ).then((res) => {
        const error = res as ValidationError
        const validation = <ReviewReviewTempAnswerValidation>validate(error, lang)
        return validation
    })
)
export const DeleteCcReviewReviewTempAnswers = (itemId: number, revisionId: number) => (
    DELETE(`/ccReviewReviewTempAnswer/delete/item/${itemId}/revision/${revisionId}`)
    .then((removedRows: number) => removedRows)
)
